import type { ProductSwatchProps } from '@components/ProductSwatch';
import type { ProductListItem } from '@providers/ProductListProvider';
import { useProductItemThemeSettings } from '@providers/ThemeProvider/Provider/ProductItemThemeSettings';
import { useMemo } from 'react';

export type ProductSwatchesPropsMapSettings = {
  [swatches: `swatches.${number}`]: Pick<
    ProductSwatchProps,
    'justifyContent' | 'options' | 'onSelect' | 'onHover' | 'itemShape'
  > & {
    changeProductImageOn?: 'click' | 'hover';
  };
};

export const useConnectProductSwatches = (place: 'img' | 'info', product: ProductListItem) => {
  const settings = useProductItemThemeSettings();
  const productElmSettings =
    place === 'img' ? settings.productImg.elements : settings.productInfo?.elements;

  const props: ProductSwatchesPropsMapSettings = useMemo(() => {
    const { options_with_values: swatches } = product;
    const productSwatchesProps: ProductSwatchesPropsMapSettings = {};
    if (productElmSettings) {
      const justifyContentPositionMap: Record<string, string> = {
        left: 'flex-start',
        center: 'center',
        right: 'flex-end',
      };

      // Maximum 3 swatches
      [1, 2, 3].forEach((index) => {
        const swatchesSettings = productElmSettings[`swatches.${index}`];
        const options = swatches?.find(
          (item) =>
            (item.original_name?.toLowerCase() || item.name) === swatchesSettings?.swatchType
        );
        if (swatchesSettings && options) {
          const position = place === 'info' ? settings.productInfo?.textAlign : undefined;

          productSwatchesProps[`swatches.${index}`] = {
            justifyContent: position && justifyContentPositionMap[position],
            options: options.values.map((value) => ({
              value: value.origin_title || value.title,
              label: value.title,
              id: `product-swatch-${product.id}-${value.title}`,
              labelAs: swatchesSettings.swatchBy,
              displayType: swatchesSettings.shape,
              showOptionValueOnHovering: swatchesSettings.showOptionValueOnHovering,
              image: value.image
                ? (product.original_images ? product.original_images : product.images_info)?.find(
                    (item) => item.position === value.image
                  )?.src
                : undefined,
            })),
            itemShape: swatchesSettings.shape,
            changeProductImageOn: swatchesSettings.changeProductImageOn,
          };
        }
      });
    }
    return productSwatchesProps;
  }, [productElmSettings, product]);

  return {
    props,
  };
};
